/**
 * An address, rendered as a link that opens Google Maps on that exact place.
 *
 * Falls back to plain text when there is no address and nothing to point at,
 * so a caller can use this everywhere an address appears without first
 * checking whether the deal actually has one.
 */
import { MapPin } from 'lucide-react'
import clsx from 'clsx'

import { mapsUrl, type MapsTarget } from './mapsUrl'

type Props = MapsTarget & {
  /** What to show. Defaults to the address itself. */
  children?: React.ReactNode
  /** Shown when there is no address at all. */
  fallback?: React.ReactNode
  className?: string
  /** A small pin ahead of the text. */
  icon?: boolean
}

export function AddressLink({ address, lat, lng, children, fallback = '—', className, icon = false }: Props) {
  const href = mapsUrl({ address, lat, lng })
  const label = children ?? (typeof address === 'string' ? address.trim() : '')

  if (!href || !label) {
    return <span className={className}>{label || fallback}</span>
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title="Open in Google Maps"
      className={clsx(
        'inline-flex items-center gap-1 underline decoration-dotted underline-offset-2 hover:text-amber-200',
        className,
      )}
    >
      {icon ? <MapPin className="h-3 w-3 flex-shrink-0 opacity-70" aria-hidden /> : null}
      <span>{label}</span>
    </a>
  )
}
